import React from "react";
import { Link } from "react-router-dom";
import { isCollectorEnabled } from "../../../helpers/collectorSession";
import SiteLayout, { SUPPORT_WHATSAPP } from "./SiteLayout";
import { TAG_PRICES_INR, formatAsOf, formatInr, formatUsd, useUsdRate } from "./usdRate";

const TRIAL_MODULES = ["Catalogue", "Karkhana", "Orders & capture", "Geo, GI & looms", "Saree costing", "AI enrichment"];

const PricingPage = () => {
  const { inrPerUsd, asOf, live } = useUsdRate();
  const usd = (inr: number) => formatUsd(inr, inrPerUsd);

  return (
    <SiteLayout title="Pricing — HastKala Pramanak">
      <section className="c-hero" style={{ paddingBottom: 0 }}>
        <div className="s-wrap">
          <span className="s-eyebrow">Pricing</span>
          <h1 style={{ maxWidth: 900 }}>
            Start free. <span className="s-grad">Pay for what you keep.</span>
          </h1>
          <p className="s-lead" style={{ maxWidth: 680 }}>
            Every new studio gets every module for 7 days. After that, Studio core stays on and you keep only the
            modules you use. Tags are a one-time cost per piece.
          </p>
        </div>
      </section>

      <section className="s-section">
        <div className="s-wrap">
          <div className="c-pcards">
            <div className="c-pc">
              <span className="c-pc__k">Studio core</span>
              <h3>Records, certificates, tags</h3>
              <div className="c-amt"><b>Included</b></div>
              <div className="c-inr">always on</div>
              <ul>
                <li>Product records</li>
                <li>Authenticity certificates</li>
                <li>NFC &amp; QR tag binding</li>
                <li>Public verification</li>
                <li>Team users</li>
              </ul>
              <Link className="s-btn s-btn--ghost" to="/studio/signup">Create organisation</Link>
            </div>
            <div className="c-pc c-pc--hl">
              <span className="c-ribbon">Start here</span>
              <span className="c-pc__k">Free trial</span>
              <h3>Every module, no card</h3>
              <div className="c-amt"><b>7 days</b></div>
              <div className="c-inr">then keep what you use</div>
              <ul>
                {TRIAL_MODULES.map((m) => <li key={m}>{m}</li>)}
              </ul>
              <Link className="s-btn s-btn--primary" to="/studio/signup">Start free studio</Link>
            </div>
            <div className="c-pc">
              <span className="c-pc__k">After the trial</span>
              <h3>Choose modules per studio</h3>
              <div className="c-amt"><b style={{ fontSize: "var(--t4)" }}>Per module</b></div>
              <div className="c-inr">priced for your studio size</div>
              <ul>
                <li>Pay in Studio → Profile → Studio modules</li>
                <li>Or ask us to switch them on for you</li>
                <li>Add or drop a module any time</li>
              </ul>
              <a className="s-btn s-btn--ghost" href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">Ask for a quote</a>
            </div>
          </div>
        </div>
      </section>

      <section className="s-section c-pricing s-section--tight" id="tags" style={{ paddingTop: 0 }}>
        <div className="s-wrap">
          <div className="s-head">
            <div><span className="s-eyebrow">Tags</span><h2>One tag per piece. Cents to prove it.</h2></div>
            <p className="s-lead">For studios tagging what they make, and collectors tagging what they keep. One-time, for the life of the piece.</p>
          </div>
          <div className="c-pcards">
            <div className="c-pc">
              <span className="c-pc__k">QR label</span>
              <h3>Printed label, sewn or tagged on</h3>
              <div className="c-amt"><b>{usd(TAG_PRICES_INR.qr)}</b><span>per piece</span></div>
              <div className="c-inr">{formatInr(TAG_PRICES_INR.qr)} · one-time</div>
              <ul>
                <li>Unique code bound to one piece</li>
                <li>Scans with any phone camera</li>
                <li>Good for everyday and gift pieces</li>
              </ul>
              <a className="s-btn s-btn--ghost" href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">Order QR labels</a>
            </div>
            <div className="c-pc c-pc--hl">
              <span className="c-ribbon">Best for high-value work</span>
              <span className="c-pc__k">NFC tag</span>
              <h3>Smart chip, tap to verify</h3>
              <div className="c-amt"><b>{usd(TAG_PRICES_INR.nfc)}</b><span>per piece</span></div>
              <div className="c-inr">{formatInr(TAG_PRICES_INR.nfc)} · one-time</div>
              <ul>
                <li>Tap with a phone — no camera, no app</li>
                <li>Locked to the piece once issued</li>
                <li>Hardest to copy or swap</li>
              </ul>
              <a className="s-btn s-btn--primary" href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">Order NFC tags</a>
            </div>
            <div className="c-pc">
              <span className="c-pc__k">My Collection</span>
              <h3>For people who keep pieces</h3>
              <div className="c-amt"><b>$0</b><span>free plan</span></div>
              <div className="c-inr">phone login, no card</div>
              <ul>
                <li>Save every tagged piece you scan</li>
                <li>Add every piece you already own, no limit</li>
                <li>Ownership history &amp; care guides</li>
              </ul>
              {isCollectorEnabled() ? (
                <Link className="s-btn s-btn--ghost" to="/collectors">For collectors</Link>
              ) : (
                <a className="s-btn s-btn--ghost" href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">Ask about My Collection</a>
              )}
            </div>
          </div>
          <p className="c-note">
            USD at {live ? "today's" : "a reference"} rate <span className="s-mono">₹{inrPerUsd.toFixed(2)} = $1</span>
            {live && asOf ? ` (${formatAsOf(asOf)})` : ""} · refreshed daily · billed in INR. Taxes and shipping extra.
          </p>
        </div>
      </section>

      <section className="s-final s-reveal">
        <div className="s-wrap">
          <span className="s-eyebrow">Questions about pricing?</span>
          <h2>Tell us about your studio. We'll size it with you.</h2>
          <div className="s-ctas">
            <Link className="s-btn s-btn--primary" to="/studio/signup">Start free studio</Link>
            <a className="s-btn s-btn--ghost" href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">Talk to us on WhatsApp</a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default PricingPage;
